import { SxProps, Theme } from "@mui/material";

const GRADIENT_CYAN = "#28C9DC";
const GRADIENT_PURPLE = "#7B49C1";

export const overlayStyles: SxProps<Theme> = {
  position: "fixed",
  inset: 0,
  zIndex: 14000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  p: 2,
  boxSizing: "border-box",
  backgroundColor: "rgba(0, 0, 0, 0.55)",
  backdropFilter: "blur(2px)",
};

export const panelStyles = (isDark: boolean): SxProps<Theme> => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: { xs: "24px", sm: "28px" },
  px: { xs: 3, sm: 4.5 },
  pt: { xs: 3.5, sm: 4.5 },
  pb: { xs: 3, sm: 3.5 },
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: { xs: 2.5, sm: 3 },
  width: "100%",
  maxWidth: { xs: 360, sm: 420 },
  maxHeight: "calc(100dvh - 32px)",
  boxSizing: "border-box",
  ...(isDark
    ? {
        color: "#FFFFFF",
        backgroundColor: "rgba(8, 12, 24, 0.92)",
        border: "1.5px solid transparent",
        backgroundImage: `
          linear-gradient(145deg, rgba(8, 12, 24, 0.96), rgba(12, 18, 36, 0.94)),
          linear-gradient(135deg, ${GRADIENT_CYAN}, ${GRADIENT_PURPLE})
        `,
        backgroundOrigin: "border-box",
        backgroundClip: "padding-box, border-box",
        boxShadow:
          "0 24px 64px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(40, 201, 220, 0.12)",
      }
    : {
        color: "#0F172A",
        backgroundColor: "#FFFFFF",
        border: "1px solid rgba(15, 23, 42, 0.06)",
        boxShadow: "0 24px 64px rgba(15, 23, 42, 0.18)",
      }),
});

export const waveDecorStyles = (isDark: boolean): SxProps<Theme> => ({
  position: "absolute",
  right: { xs: -40, sm: -20 },
  top: { xs: 24, sm: 16 },
  width: { xs: 180, sm: 220 },
  height: { xs: 180, sm: 220 },
  pointerEvents: "none",
  opacity: isDark ? 0.55 : 0.45,
  background: `
    radial-gradient(ellipse at 30% 40%, rgba(40, 201, 220, 0.35) 0%, transparent 55%),
    radial-gradient(ellipse at 70% 60%, rgba(123, 73, 193, 0.35) 0%, transparent 55%),
    radial-gradient(ellipse at 50% 80%, rgba(99, 102, 241, 0.2) 0%, transparent 50%)
  `,
  filter: "blur(2px)",
});

export const contentStyles: SxProps<Theme> = {
  position: "relative",
  zIndex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  gap: { xs: 2.5, sm: 3 },
};

export const iconWrapStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mb: 0.5,
};

export const titleStyles = (isDark: boolean): SxProps<Theme> => ({
  fontSize: { xs: "1.35rem", sm: "1.5rem" },
  fontWeight: 700,
  textAlign: "center",
  lineHeight: 1.25,
  letterSpacing: "-0.02em",
  color: isDark ? "#FFFFFF" : "#0F172A",
  px: 1,
});

export const subtitleStyles = (isDark: boolean): SxProps<Theme> => ({
  fontSize: { xs: "0.95rem", sm: "1.05rem" },
  fontWeight: 400,
  textAlign: "center",
  lineHeight: 1.4,
  color: isDark ? "rgba(255, 255, 255, 0.62)" : "#6B7280",
  mt: 0.75,
  px: 1,
});

export const actionsRowStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  gap: { xs: 2, sm: 3 },
  width: "100%",
  mt: { xs: 0.5, sm: 1 },
};

export const actionItemStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 1.25,
  minWidth: { xs: 72, sm: 88 },
  maxWidth: 110,
  p: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  "&:disabled": {
    opacity: 0.45,
    cursor: "default",
  },
};

export type ActionTone = "primary" | "neutral" | "danger";

export const actionCircleStyles = (
  isDark: boolean,
  tone: ActionTone,
): SxProps<Theme> => {
  const base = {
    width: { xs: 56, sm: 64 },
    height: { xs: 56, sm: 64 },
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFFFFF",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
    ".MuiButtonBase-root:hover &, button:hover &": {
      transform: "scale(1.04)",
    },
  };

  if (tone === "primary") {
    return {
      ...base,
      background: "linear-gradient(180deg, #4C8DFF 0%, #2F6BFF 100%)",
      boxShadow: isDark
        ? "0 8px 24px rgba(47, 107, 255, 0.45)"
        : "0 8px 20px rgba(47, 107, 255, 0.35)",
    };
  }

  if (tone === "danger") {
    return {
      ...base,
      background: "linear-gradient(180deg, #FF5A5A 0%, #E11D2E 100%)",
      boxShadow: isDark
        ? "0 8px 24px rgba(225, 29, 46, 0.45)"
        : "0 8px 20px rgba(225, 29, 46, 0.3)",
    };
  }

  return {
    ...base,
    background: isDark ? "rgba(255, 255, 255, 0.16)" : "#A8B0BC",
    boxShadow: isDark
      ? "0 8px 20px rgba(0, 0, 0, 0.35)"
      : "0 6px 16px rgba(15, 23, 42, 0.12)",
  };
};

export const actionLabelStyles = (isDark: boolean): SxProps<Theme> => ({
  fontSize: { xs: "0.75rem", sm: "0.8125rem" },
  fontWeight: 500,
  textAlign: "center",
  lineHeight: 1.25,
  color: isDark ? "rgba(255, 255, 255, 0.92)" : "#374151",
  maxWidth: 96,
});
